import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GraduationCap, Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const seedAdmin = async () => {
      try {
        await axios.post(`${API_URL}/seed-admin`);
      } catch (error) {
        // Admin might already exist
      }
    };
    seedAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`Bienvenido, ${user.full_name}`);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A1128]">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/dd73e03a-b9ba-458e-a8df-3bcb223cc525/images/0107cee39d9d044992e93c7314ab009421a5e03fafb13c0939d2b49c616b06cd.png"
          alt="Consultor profesional"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1128]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A1128]" />

        <div className="relative h-full flex flex-col justify-between p-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors w-fit"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="login-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md"
          >
            <div className="w-16 h-16 mb-8 rounded-2xl gold-gradient flex items-center justify-center gold-glow">
              <GraduationCap className="w-8 h-8 text-[#0A1128]" />
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold mb-6 tracking-tight text-white leading-tight">
              Centro Formativo <span className="gold-gradient-text italic">URCAL</span>
            </h1>
            <p className="text-slate-300 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              El campus virtual para consultores y freelancers: formación práctica, Tutor Virtual con IA
              y recursos para construir tu libertad financiera.
            </p>
            <div className="mt-10 flex gap-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="px-4 py-2 glass-panel rounded-full text-xs text-slate-200">
                Vídeos formativos
              </div>
              <div className="px-4 py-2 glass-panel rounded-full text-xs text-slate-200">
                Tutor Virtual IA
              </div>
              <div className="px-4 py-2 glass-panel rounded-full text-xs text-slate-200">
                Material descargable
              </div>
            </div>
          </motion.div>

          <p className="text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            © {new Date().getFullYear()} Centro Formativo URCAL
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-yellow-500/8 rounded-full blur-[100px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md relative"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl gold-gradient flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-[#0A1128]" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              Centro Formativo URCAL
            </h1>
          </div>

          <div className="glass-card rounded-2xl p-8 md:p-10">
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Campus Virtual
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                Iniciar Sesión
              </h2>
              <p className="text-sm text-slate-400 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Introduce tus credenciales de alumno para acceder
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Tu nombre de usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#D4AF37]"
                  data-testid="login-username"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pr-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#D4AF37]"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 gold-gradient text-[#0A1128] font-bold text-base hover:opacity-90 btn-active shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Iniciando...
                  </>
                ) : (
                  "Acceder al Campus"
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              El acceso al campus lo proporciona el Centro Formativo URCAL.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
